import React from "react";
import { ShieldCheck, UserPlus, ClipboardList, Video, Pill } from "lucide-react";

const HowItWorks = () => {
  const steps = [
    {
      id: 1,
      title: "Check Your Eligibility",
      desc: "Complete a short questionnaire to find out if you may be eligible for medical cannabis treatment. It takes less than 30 seconds.",
      icon: <ShieldCheck className="w-12 h-12 text-white" />,
      color: "bg-gradient-to-br from-[#005eb8] to-[#00aeef]",
      shadow: "shadow-blue-200"
    },
    {
      id: 2,
      title: "Create a Patient Account",
      desc: "Register securely on our patient portal to begin your application and manage your care in one place.",
      icon: <UserPlus className="w-12 h-12 text-white" />,
      color: "bg-gradient-to-br from-[#2d8c6e] to-[#4caf50]",
      shadow: "shadow-green-200"
    },
    {
      id: 3,
      title: "Complete Your Profile",
      desc: "Provide your medical history and upload relevant documents so our clinical team can review your case thoroughly.",
      icon: <ClipboardList className="w-12 h-12 text-white" />,
      color: "bg-gradient-to-br from-[#2ba78c] to-[#76c042]",
      shadow: "shadow-teal-200"
    },
    {
      id: 4,
      title: "Initial Consultation",
      desc: "Meet with one of our specialist clinicians via a secure video appointment. They will review your history and discuss your treatment options.",
      icon: <Video className="w-12 h-12 text-white" />,
      color: "bg-gradient-to-br from-[#012d5c] to-[#1a2e4a]",
      shadow: "shadow-indigo-200"
    },
    {
      id: 5,
      title: "Prescription Issued",
      desc: "Following a multi-disciplinary team (MDT) review and clinical approval, a prescription may be issued where appropriate. Medication is delivered discreetly to your door.",
      icon: <Pill className="w-12 h-12 text-white" />,
      color: "bg-gradient-to-br from-[#6b6be0] to-[#8ac2f3]",
      shadow: "shadow-purple-200"
    },
  ];

  return (
    <section className="py-24 bg-[#f0f4f8]" id="how-it-works">
      <div className="container mx-auto px-4">
        {/* Heading */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-[#012d5c]">
            How it works
          </h2>
          <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
            A clear, clinically-led pathway from initial eligibility check to receiving your treatment.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {steps.map((step) => (
            <div key={step.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden flex flex-col h-full border border-gray-50 group">

              {/* Top Half: Icon Area */}
              <div className="bg-[#f8fafc] h-44 flex items-center justify-center border-b border-gray-100 relative overflow-hidden">
                <div className={`absolute w-40 h-40 rounded-full opacity-10 ${step.color} scale-0 group-hover:scale-150 transition-transform duration-500 ease-out`}></div>
                <div className={`${step.color} p-5 rounded-3xl shadow-lg ${step.shadow} transform group-hover:scale-110 transition-transform duration-300`}>
                  {step.icon}
                </div>
              </div>

              {/* Bottom Half: Content */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start gap-3 mb-3">
                  <div className="shrink-0 w-7 h-7 bg-[#1a2e4a] text-white rounded-full flex items-center justify-center font-bold text-xs shadow-md">
                    {step.id}
                  </div>
                  <h3 className="text-base font-bold text-[#1a2e4a] leading-tight pt-0.5">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed pl-10">
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-gray-400 mt-8">
          Prescription issuance is subject to MDT review and clinical approval. A prescription is not guaranteed.
        </p>
      </div>
    </section>
  );
};

export default HowItWorks;
